import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiChevronUp, FiCheck, FiSearch } from "react-icons/fi";
import "../style/Dropdown.css";

const VIEWPORT_GAP = 8;
const MENU_GAP = 5;
const MIN_MENU_HEIGHT = 80;
const DEFAULT_MENU_HEIGHT = 280;

const AppDropdown = ({
  value,
  options = [],
  onChange,
  placeholder = "Select...",
  position = "below",
  disabled = false,
  menuWidth = null,
  searchable = false,
  minSearchChars = 2,
  searchPlaceholder = null,
  creatable = false,
  onSearch = null,
  loadingSearch = false,
  menuClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: DEFAULT_MENU_HEIGHT,
  });

  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selectedOption = options.find((option) => option.value === value);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const availableBelow = Math.max(
      0,
      viewportHeight - rect.bottom - VIEWPORT_GAP - MENU_GAP
    );
    const availableAbove = Math.max(
      0,
      rect.top - VIEWPORT_GAP - MENU_GAP
    );

    // "below" and "above" are preferred directions, not forced directions.
    const preferredAbove = position === "above";

    let openAbove;
    if (preferredAbove) {
      openAbove = availableAbove >= MIN_MENU_HEIGHT || availableAbove > availableBelow;
    } else {
      openAbove = availableBelow < MIN_MENU_HEIGHT && availableAbove > availableBelow;
    }

    const availableSpace = openAbove ? availableAbove : availableBelow;

    // Never force a menu taller than the visible viewport space.
    const maxHeight = Math.max(
      1,
      Math.min(DEFAULT_MENU_HEIGHT, availableSpace)
    );

    const requestedWidth = menuWidth || rect.width;
    const width = Math.min(
      requestedWidth,
      viewportWidth - VIEWPORT_GAP * 2
    );

    let left = rect.left;

    if (left + width > viewportWidth - VIEWPORT_GAP) {
      left = viewportWidth - width - VIEWPORT_GAP;
    }

    if (left < VIEWPORT_GAP) {
      left = VIEWPORT_GAP;
    }

    let top = openAbove
      ? rect.top - maxHeight - MENU_GAP
      : rect.bottom + MENU_GAP;

    // Final viewport clamp. This prevents the top/bottom of the menu
    // from disappearing even when both spaces are small.
    top = Math.max(
      VIEWPORT_GAP,
      Math.min(top, viewportHeight - maxHeight - VIEWPORT_GAP)
    );

    setMenuStyle({
      position: "fixed",
      top,
      left,
      width,
      maxHeight,
    });
  }, [position, menuWidth]);

  const handleTriggerClick = (event) => {
    event.stopPropagation();
    if (disabled) return;
    if (!isOpen) setQuery("");
    setIsOpen((previous) => !previous);
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePositionUpdate = () => updateMenuPosition();

    // Capture scroll events from the page, MUI containers and sidebar.
    window.addEventListener("scroll", handlePositionUpdate, true);
    window.addEventListener("resize", handlePositionUpdate);

    return () => {
      window.removeEventListener("scroll", handlePositionUpdate, true);
      window.removeEventListener("resize", handlePositionUpdate);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      const clickedTrigger = triggerRef.current?.contains(event.target);
      const clickedMenu = menuRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (searchable && query) {
          setQuery("");
          return;
        }
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, query, searchable]);

  const handleOptionClick = (optionValue) => {
    if (disabled) return;
    onChange?.(optionValue);
    setIsOpen(false);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    const trimmed = value.trim();
    if (onSearch && trimmed.length >= minSearchChars) onSearch(trimmed);
  };

  const trimmedQuery = query.trim().toLowerCase();
  const searchActive = searchable && trimmedQuery.length >= minSearchChars;
  const asyncSearch = searchable && Boolean(onSearch);
  const filteredOptions = searchActive
    ? options.filter((option) => String(option.label ?? "").toLowerCase().includes(trimmedQuery))
    : options;
  const hasExactMatch = options.some(
    (option) => String(option.label ?? "").toLowerCase() === trimmedQuery
  );
  const createOption =
    creatable && searchActive && !hasExactMatch
      ? [{ value: query.trim(), label: `Use "${query.trim()}"`, isCreate: true }]
      : [];
  const visibleOptions = [...filteredOptions, ...createOption];

  const menu = isOpen
    ? createPortal(
        <div
          ref={menuRef}
          className={`dropdown-popup-portal ${menuClassName}`.trim()}
          style={menuStyle}
          role="listbox"
        >
          {searchable && (
            <div className="dropdown-search-wrap">
              <span className="dropdown-search-icon" aria-hidden="true">
                <FiSearch size={14} />
              </span>
              <input
                className="dropdown-search-input"
                type="text"
                value={query}
                onChange={handleSearchChange}
                onClick={(event) => event.stopPropagation()}
                placeholder={searchPlaceholder || `Type ${minSearchChars} letters to search...`}
                aria-label="Search options"
                autoFocus
              />
            </div>
          )}

          {loadingSearch && searchActive ? (
            <div className="dropdown-empty">Searching...</div>
          ) : (
            visibleOptions.length === 0 ? (
              <div className="dropdown-empty">
                {searchable && !searchActive && asyncSearch && options.length === 0
                  ? `Type ${minSearchChars} letters to search`
                  : options.length === 0 ? (searchable ? "Type to search" : "No options available")
                  : "No matching options"}
              </div>
            ) : (
              visibleOptions.map((option) => (
                <div
                  key={String(option.value)}
                  className={`dropdown-option ${value === option.value ? "selected" : ""} ${option.isCreate ? "dropdown-option-create" : ""}`}
                  onClick={() => handleOptionClick(option.value)}
                  role="option"
                  aria-selected={value === option.value}
                >
                  <span className="dropdown-option-label">
                    {option.label}
                  </span>

                  {value === option.value && (
                    <span className="dropdown-selected-icon" aria-hidden="true">
                      <FiCheck size={14} />
                    </span>
                  )}
                </div>
              ))
            )
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div className="dropdown-wrapper">
        <div
          ref={triggerRef}
          className={`app-dropdown-trigger ${
            isOpen ? "is-open" : ""
          } ${disabled ? "is-disabled" : ""}`}
          onClick={handleTriggerClick}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={isOpen}
          aria-disabled={disabled}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleTriggerClick(event);
            }
          }}
        >
          <span className="app-dropdown-value">
            {selectedOption ? selectedOption.label : placeholder}
          </span>

          <span className="app-dropdown-icon" aria-hidden="true">
            {isOpen ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
          </span>
        </div>
      </div>

      {menu}
    </>
  );
};

export default AppDropdown;
